import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Typography,
  Chip,
  Box,
  Divider,
  Alert,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import type { Risk, AlertType } from "../../risks/types";
import type { Sighting } from "../../sightings/types";
import type { WorkOrder, WorkOrderType, WorkOrderPriority } from "../types";
import {
  technicians,
  addWorkOrder,
  generateWorkOrderId,
} from "../data/workOrdersData";

// ── Type / Priority mapping ───────────────────────────────────────────────────

const alertTypeToWorkOrderType: Record<AlertType, WorkOrderType> = {
  leak: "other",
  pressure: "other",
  intrusion: "earth_moving",
  equipment: "network_works",
  environmental: "flooding",
};

const severityToPriority: Record<string, WorkOrderPriority> = {
  critical: "critical",
  high: "high",
  medium: "medium",
  low: "low",
};

const workOrderTypeLabels: Record<WorkOrderType, string> = {
  earth_moving: "Earth Moving",
  road_works: "Road Works",
  building_works: "Building Works",
  machinery_parked: "Machinery Parked",
  agi_activity: "AGI Activity",
  marker_posts: "Marker Posts",
  forestry: "Forestry",
  quarry: "Quarry",
  flooding: "Flooding",
  ditching: "Ditching",
  network_works: "Network Works",
  fencing: "Fencing",
  other: "Other",
  unidentified: "Unidentified",
};

const priorityOptions: WorkOrderPriority[] = ["critical", "high", "medium", "low"];

const allWorkOrderTypes: WorkOrderType[] = [
  "earth_moving", "road_works", "building_works", "machinery_parked",
  "agi_activity", "marker_posts", "forestry", "quarry", "flooding",
  "ditching", "network_works", "fencing", "other", "unidentified",
];

// ── Helper ────────────────────────────────────────────────────────────────────

const generateMapReference = (): string => {
  const letters = ["SP", "SK", "TL", "TF", "SE", "NZ"];
  const letter = letters[Math.floor(Math.random() * letters.length)];
  const easting = Math.floor(Math.random() * 90000 + 10000);
  const northing = Math.floor(Math.random() * 90000 + 10000);
  return `${letter} ${easting} ${northing}`;
};

const getSLAHours = (priority: WorkOrderPriority): number => {
  switch (priority) {
    case "critical": return 1;
    case "high": return 4;
    case "medium": return 24;
    case "low": return 72;
  }
};

// ── Component ─────────────────────────────────────────────────────────────────

interface CreateWorkOrderDialogProps {
  open: boolean;
  onClose: () => void;
  fromRisk?: Risk;
  fromSighting?: Sighting;
}

export const CreateWorkOrderDialog = ({
  open,
  onClose,
  fromRisk,
  fromSighting,
}: CreateWorkOrderDialogProps) => {
  const navigate = useNavigate();

  // Derive initial values from source
  const initialType: WorkOrderType = fromRisk
    ? alertTypeToWorkOrderType[fromRisk.type]
    : fromSighting
      ? fromSighting.type
      : "other";

  const initialPriority: WorkOrderPriority = fromRisk
    ? severityToPriority[fromRisk.severity] || "medium"
    : "medium";

  const initialDescription = fromRisk
    ? `${fromRisk.title}. ${fromRisk.description}`
    : fromSighting
      ? fromSighting.description
      : "";

  const location = fromRisk
    ? fromRisk.location
    : fromSighting
      ? fromSighting.location
      : "";

  const coordinates: [number, number] = fromRisk
    ? fromRisk.coordinates
    : fromSighting
      ? fromSighting.coordinates
      : [0, 0];

  const zone = fromRisk
    ? (fromRisk.region === "West" ? "W1" : fromRisk.region === "East" ? "E1" : "SN1")
    : fromSighting
      ? fromSighting.zone
      : "";

  const mapReference = fromSighting
    ? fromSighting.mapReference
    : generateMapReference();

  // Form state
  const [type, setType] = useState<WorkOrderType>(initialType);
  const [priority, setPriority] = useState<WorkOrderPriority>(initialPriority);
  const [description, setDescription] = useState(initialDescription);
  const [technicianId, setTechnicianId] = useState("");

  // Reset form when dialog opens with new source
  useEffect(() => {
    if (open) {
      setType(initialType);
      setPriority(initialPriority);
      setDescription(initialDescription);
      setTechnicianId("");
    }
  }, [open, fromRisk?.id, fromSighting?.id]);

  const sourceLabel = fromRisk
    ? `Risk ${fromRisk.id}`
    : fromSighting
      ? `Sighting ${fromSighting.id}`
      : "";

  const handleCreate = () => {
    const now = new Date();
    const id = generateWorkOrderId();
    const slaHours = getSLAHours(priority);
    const slaDeadline = new Date(now.getTime() + slaHours * 60 * 60 * 1000);
    const assignedTech = technicianId
      ? technicians.find((t) => t.id === technicianId)
      : undefined;

    const newWorkOrder: WorkOrder = {
      id,
      referenceNumber: `2601W${zone} ${id.replace("WO-", "")}`,
      alertId: fromRisk?.id,
      sightingId: fromSighting?.id,
      type,
      status: "open",
      priority,
      location,
      mapReference,
      coordinates,
      zone,
      description,
      assignedTechnician: assignedTech,
      createdAt: now.toISOString(),
      slaDeadline: slaDeadline.toISOString(),
      slaBreached: false,
      notes: [],
      actions: [
        {
          id: "action-1",
          type: "create",
          actor: "Control Room",
          description: `Work order created from ${sourceLabel}`,
          timestamp: now.toISOString(),
        },
      ],
    };

    addWorkOrder(newWorkOrder);
    onClose();
    navigate(`/work-orders/${id}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AssignmentIcon color="primary" />
          <Typography variant="h6">Create Work Order</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* Source info */}
          <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
            Creating work order from <strong>{sourceLabel}</strong>
          </Alert>

          {/* Location (read-only) */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <LocationIcon fontSize="small" color="action" />
              <Typography variant="caption" color="text.secondary">Location</Typography>
            </Stack>
            <Typography variant="body2">{location}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip label={zone} size="small" variant="outlined" />
              <Chip
                label={`${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)}`}
                size="small"
                variant="outlined"
                sx={{ fontFamily: "monospace", fontSize: 11 }}
              />
            </Stack>
          </Box>

          <Divider />

          {/* Type */}
          <TextField
            select
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value as WorkOrderType)}
            size="small"
            fullWidth
          >
            {allWorkOrderTypes.map((t) => (
              <MenuItem key={t} value={t}>
                {workOrderTypeLabels[t]}
              </MenuItem>
            ))}
          </TextField>

          {/* Priority */}
          <TextField
            select
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value as WorkOrderPriority)}
            size="small"
            fullWidth
          >
            {priorityOptions.map((p) => (
              <MenuItem key={p} value={p}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Chip
                    label={p.charAt(0).toUpperCase() + p.slice(1)}
                    size="small"
                    color={
                      p === "critical" ? "error" :
                      p === "high" ? "warning" :
                      p === "medium" ? "info" : "success"
                    }
                    sx={{ minWidth: 70 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    SLA: {getSLAHours(p)}h
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </TextField>

          {/* Description */}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={3}
          />

          {/* Technician */}
          <TextField
            select
            label="Assign Technician"
            value={technicianId}
            onChange={(e) => setTechnicianId(e.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value="">
              <em>Unassigned</em>
            </MenuItem>
            {technicians.map((tech) => (
              <MenuItem key={tech.id} value={tech.id}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                  <Typography variant="body2">{tech.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {tech.zone}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Chip
                    label={tech.status}
                    size="small"
                    color={
                      tech.status === "available" ? "success" :
                      tech.status === "busy" ? "warning" : "default"
                    }
                    variant="outlined"
                    sx={{ fontSize: 11 }}
                  />
                </Stack>
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={!description.trim()}
          startIcon={<AssignmentIcon />}
        >
          Create Work Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};
